var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import axios from 'axios';
import useSWR from 'swr';
import { defaultConfig as defaultSwrConfig } from 'swr/_internal';
import { decodeJwt, getCustomerFromToken, getDistributionChannel, getPensionBaseUrl, HAS_FIXED_DEPOSIT_ACCOUNT, HAS_NOTICE_DEPOSIT_ACCOUNT, HAS_OVERNIGHT_DEPOSIT_ACCOUNT, isCSR, isCustomerLoggedIn, isDEUCustomer, isGBRRegion, useFeatureFlag, useMainCustomer, useTransactionAccount, } from '@raisin/cp-utils';
import localStorage from 'store2';
import { createAsyncAction, METHODS } from '../api';
import { AUTH_TOKEN_KEY, DEFAULT_DISTRIBUTOR } from '../constants';
import { getCustomerData } from '../utils/customer/layoutMapper';
import { getBaseUrlForDbs, getBaseUrlForPia, getBaseUrlForPom, integrationSerApiBaseUrl, } from '../utils/MicroServices';
const accessKeyMapping = {
    overnight_account: HAS_OVERNIGHT_DEPOSIT_ACCOUNT,
    notice_account: HAS_NOTICE_DEPOSIT_ACCOUNT,
    term_deposit: HAS_FIXED_DEPOSIT_ACCOUNT,
};
const getCustomerEmbedItems = () => [
    'all-persons',
    'reference-accounts',
    ...(!isGBRRegion() ? ['data-validity'] : []),
];
const mapDbsAccessToObsAccess = (dbsAccess) => {
    const result = [];
    if (dbsAccess) {
        for (const key in dbsAccess) {
            if (dbsAccess[key] === true) {
                if (accessKeyMapping[key]) {
                    result.push(accessKeyMapping[key]);
                }
            }
        }
    }
    return result;
};
const SWR_CONFIG = {
    dedupingInterval: 300000,
    onErrorRetry: (error, key, config, revalidate, revalidateOpts) => {
        var _a, _b;
        // If the endpoint returns 404 (Not Found) or 410 (Gone) don't keep
        // retrying, as it's not likely that the state will change during the lifetime of the page.
        if (axios.isAxiosError(error) &&
            (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 404 || ((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === 410)) {
            return;
        }
        // Otherwise, just use the default handling
        return defaultSwrConfig.onErrorRetry(error, key, config, revalidate, revalidateOpts);
    },
};
export const getDistributorForCustomer = () => {
    try {
        const auth = localStorage(AUTH_TOKEN_KEY);
        const token = auth === null || auth === void 0 ? void 0 : auth.access_token;
        const { distributor } = decodeJwt(token);
        return distributor;
    }
    catch (error) {
        return DEFAULT_DISTRIBUTOR;
    }
};
const getCustomerCobranding = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const baseUrl = integrationSerApiBaseUrl();
        return yield createAsyncAction('customer.cobranding', METHODS.GET, baseUrl, true);
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error('error to fetch customer.cobranding', e);
    }
    return null;
});
const useDbsDepositsAccess = (isLoggedIn, customer) => useSWR(isLoggedIn && (customer === null || customer === void 0 ? void 0 : customer.id) ? 'deposits.dbsDepositsTypes' : null, () => createAsyncAction('deposits.dbsDepositsTypes', METHODS.GET, getBaseUrlForDbs(), true, {
    params: { customer_id: customer === null || customer === void 0 ? void 0 : customer.id },
}), SWR_CONFIG);
const useCustomerCobranding = (isLoggedIn, distributor) => useSWR(isLoggedIn && distributor && distributor !== 'Raisin' ? 'customer.cobranding' : null, getCustomerCobranding, SWR_CONFIG);
const usePomProducts = (isDEUCustomerAccount, isLoggedIn) => {
    const pomBaseUrl = isCSR() ? getBaseUrlForPom() : 'production';
    const { data = null, isLoading, error, mutate, } = useSWR(isDEUCustomerAccount && isLoggedIn ? 'pom.products' : null, () => createAsyncAction('pom.products', 'get', pomBaseUrl, true), SWR_CONFIG);
    const pomProducts = {
        data,
        isLoading,
        error,
        mutate,
    };
    return pomProducts;
};
const usePiaProducts = (isDEUCustomerAccount, isLoggedIn, config) => {
    const piaBaseUrl = isCSR() ? getBaseUrlForPia() : 'production';
    // NOTE: This endpoint can return 404 as a result, that at the moment is treated as an error
    const { data: piaProductInstances = null } = useSWR(isDEUCustomerAccount && isLoggedIn && config ? 'pia.productInstances' : null, () => createAsyncAction('pia.productInstances', 'get', piaBaseUrl, true, {
        params: { customer_domain_id: config.params.customerId, pageId: config.params.pageId },
    }), SWR_CONFIG);
    return piaProductInstances;
};
export const useProductsTypes = (isLoggedIn, distributor, distributionChannelId) => {
    const baseUrl = getBaseUrlForDbs();
    const { data, isLoading } = useSWR(isLoggedIn && distributor && distributionChannelId ? 'products.productsTypes' : null, () => createAsyncAction('products.productsTypes', 'get', baseUrl, true, {
        params: { distributor_id: distributor, distribution_channel_id: distributionChannelId },
    }), SWR_CONFIG);
    return { productsTypes: data !== null && data !== void 0 ? data : null, isLoading };
};
const usePpDashboardData = (key, customerId) => {
    // NOTE: This endpoint can return 405 as a result, that at the moment is treated as an error
    const { data: ppDashboardData = null, isLoading, isValidating, mutate, error, } = useSWR(key, (url) => 
    // pensions redirect feature flag will redirect from OBS pensions/customer/product to pensions domain /products/{bac}
    customerId
        ? createAsyncAction(url, METHODS.GET, getPensionBaseUrl(), true)
        : createAsyncAction(url), SWR_CONFIG);
    return {
        ppDashboardData,
        isLoading: isLoading || isValidating,
        mutate,
        error,
    };
};
const isHeaderDataLoading = (isFeatureFlagLoading, isLoadingCustomerData, isLoadingDBSDepositsAccess) => isFeatureFlagLoading || isLoadingCustomerData || isLoadingDBSDepositsAccess;
const useFetchHeaderData = (locale) => {
    var _a;
    const customerId = (_a = getCustomerFromToken()) === null || _a === void 0 ? void 0 : _a.bacNumber;
    const { isLoading: isFeatureFlagLoading } = useFeatureFlag();
    const [productAccess, setProductAccess] = useState([]);
    const isLoggedIn = isCustomerLoggedIn();
    const distributor = getDistributorForCustomer();
    const distributionChannelId = getDistributionChannel(locale || 'de-DE');
    const { productsTypes } = useProductsTypes(isLoggedIn, distributor, distributionChannelId);
    const [config, setConfig] = useState(null);
    const { data: cobranding = undefined } = useCustomerCobranding(isLoggedIn, distributor);
    const { customerData = null, mainCustomer, error: customerError, loading: isLoadingCustomerData = true, mutate, isValidating: isCasAPIValidating, } = useMainCustomer({
        customerId,
        locale,
        embed: getCustomerEmbedItems(),
    });
    const { data: dbsDepositsAccess = null, isLoading: isLoadingDBSDepositsAccess = true, } = useDbsDepositsAccess(isLoggedIn, customerData);
    let isDEUCustomerAccount = customerData && isDEUCustomer(customerData);
    useEffect(() => {
        const transformedDbsAccess = mapDbsAccessToObsAccess(dbsDepositsAccess);
        const hasDbsDeposits = !!(transformedDbsAccess === null || transformedDbsAccess === void 0 ? void 0 : transformedDbsAccess.length);
        if (hasDbsDeposits) {
            setProductAccess(transformedDbsAccess);
        }
    }, [dbsDepositsAccess]);
    useEffect(() => {
        if (customerData) {
            setConfig({
                params: {
                    customerId: customerData.id,
                    pageId: window.location.pathname + window.location.hash.replace('#/', ''),
                },
            });
        }
    }, [customerId, customerData]);
    const { data: transactionAccountData = null, error, mutate: refetchTransactionAccountData, } = useTransactionAccount({
        bacNumber: isLoggedIn ? customerId : null,
        withBalance: true,
        options: SWR_CONFIG,
    });
    const getPensionProductsKey = () => {
        if (isDEUCustomerAccount && isLoggedIn && !isFeatureFlagLoading) {
            return 'dashboard.pensionProductsMigrated';
        }
        return null; // disables the query for non-logged in & non DEU customers
    };
    const ppDashboardDataresponse = usePpDashboardData(getPensionProductsKey(), customerId);
    return {
        customerData: getCustomerData({
            customerData,
            mainCustomer,
        }),
        cobranding,
        ppDashboardData: ppDashboardDataresponse.ppDashboardData,
        ppDashboardResponseState: {
            isLoading: ppDashboardDataresponse.isLoading,
            error: ppDashboardDataresponse.error,
            mutate: ppDashboardDataresponse.mutate,
        },
        productAccess,
        dbsDepositsAccess,
        transactionAccountData,
        refetchTransactionAccountData,
        piaProductInstances: usePiaProducts(isDEUCustomerAccount, isLoggedIn, config),
        transactionAccountError: error,
        isHeaderDataLoading: isHeaderDataLoading(isFeatureFlagLoading, isLoadingCustomerData, isLoadingDBSDepositsAccess),
        pomProducts: usePomProducts(isDEUCustomerAccount, isLoggedIn),
        productsTypes,
        customerDataFetchState: {
            isLoading: isLoadingCustomerData,
            error: customerError,
            mutate: mutate,
            isValidating: isCasAPIValidating,
        },
    };
};
export default useFetchHeaderData;
//# sourceMappingURL=useFetchHeaderData.js.map